import { useTranslation } from 'react-i18next';

import { Box, Card, Heading, List, ListItem, Text } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { SanityImage } from '~/components/sanity-image';

export type HomepagePartnershipsSectionProps = StaticHomepagePartnershipsSectionProps;

export function HomepagePartnershipsSection({ partnerLogos }: HomepagePartnershipsSectionProps) {
  const { t } = useTranslation();
  return (
    <Box
      as='section'
      bgColor='grey.50'
      padding={{
        base: '56px 0px',
        md: '112px 0px',
      }}
    >
      <ContentContainer
        contentMaxWidth='content-max-width-md'
        display='flex'
        flexDirection='column'
        textAlign='center'
        gap={{
          base: 8,
          md: 12,
        }}
      >
        <Box margin='0 auto' maxWidth='862px' width='100%'>
          <Heading
            as='h2'
            fontWeight='600'
            lineHeight='1.1'
            marginBottom={4}
            fontSize={{
              base: '1.75rem',
              md: '2.25rem',
            }}
          >
            {t('home.partnerships.title')}
          </Heading>
          <Text color='grey.500'>{t('home.partnerships.description')}</Text>
        </Box>
        <Card
          alignItems='center'
          as={List}
          border='1px solid'
          borderColor='grey.100'
          borderRadius='8px'
          boxShadow='none'
          justifyContent='center'
          display={{
            base: 'grid',
            md: 'flex',
          }}
          flexDirection={{
            md: 'row',
          }}
          flexWrap={{
            md: 'wrap',
          }}
          gap={{
            base: 8,
            md: '48px',
          }}
          gridTemplateColumns={{
            base: 'repeat(auto-fit, minmax(min(100%, 100px), 1fr))',
            md: 'unset',
          }}
          padding={{
            base: '32px',
            md: '48px 64px',
          }}
        >
          {partnerLogos.map((partner) => {
            if (!partner.logo) return null;
            return (
              <ListItem key={partner.name} flexBasis={{ md: '20%' }}>
                <SanityImage
                  alt={partner.name}
                  image={partner.logo}
                  style={{
                    maxHeight: '36px',
                    margin: '0 auto',
                  }}
                />
              </ListItem>
            );
          })}
        </Card>
      </ContentContainer>
    </Box>
  );
}

export async function transformStaticHomepagePartnershipsSectionProps() {
  const { getCompanies } = await import('~/features/companies/controllers/get-companies');
  const partners = ['Audi', 'Vodafone', 'Elm', 'BNP Paribas', 'Koc', 'Shell', 'Accenture', 'Siemens'];
  const partnerLogos = await getCompanies(partners).then((companies) =>
    companies.filter(Boolean).map((company) => ({
      name: company.name,
      logo: company.lofiLogoDark,
    }))
  );

  return {
    partnerLogos: [...partnerLogos].sort((a, b) => partners.indexOf(a.name) - partners.indexOf(b.name)),
  };
}

export type StaticHomepagePartnershipsSectionProps = Awaited<
  ReturnType<typeof transformStaticHomepagePartnershipsSectionProps>
>;
